import { useCallback, useEffect, useState } from 'react'

import { getPlans } from 'utils/api'
import { growthBook, growthbookFeatures } from 'utils/growthbook'
import { setPlans } from 'utils/payment'

type PlanCurrencies = {
  [currencyCode: string]: number
}

type RawPlanItem = {
  currencies: PlanCurrencies
}

type RawPlanRegion = {
  [planCode: string]: RawPlanItem
}

type RawPlanInterval = {
  [region: string]: RawPlanRegion
}

type RawPlanTier = {
  [interval: string]: RawPlanInterval
}

type RawPlanData = {
  [tier: string]: RawPlanTier
}

type NormalizedRegionItem = {
  plan_code: string
  currencies: PlanCurrencies
}

type NormalizedInterval = {
  [region: string]: NormalizedRegionItem
}

type NormalizedTier = {
  [interval: string]: NormalizedInterval
}

type NormalizedPlans = {
  [tier: string]: NormalizedTier | RawPlanTier
}

type GrowthbookFeaturePlanValue = {
  plan_prefix: string
  plan_interval: string
  plan_region: string
  experiment: 'united_states' | 'standard_countries' | 'others'
  enabled: boolean
  start_date: string
  end_date: string
}

export const usePlans = () => {
  const [isLoading, setIsLoading] = useState(true)

  const fetchPlans = useCallback(async () => {
    try {
      setIsLoading(true)
      setPlans(null)

      const { data: plans } = (await getPlans()) as { data: RawPlanData }

      const value =
        growthBook.getFeatureValue<GrowthbookFeaturePlanValue | null>(
          growthbookFeatures.NEW_MONTHLY_PRICE,
          null,
        )

      if (!value) {
        return
      }

      const { plan_prefix: planPrefix, plan_interval: planInterval } = value

      const plansWithLifetimeIncrease = Object.keys(plans).reduce<RawPlanData>(
        (newPlans, currentPlanKey) => {
          const currentPlan = plans[currentPlanKey]

          if (!currentPlan[planInterval]) {
            return { ...newPlans, [currentPlanKey]: currentPlan }
          }

          const planCodeLocal = Object.keys(
            currentPlan[planInterval].local,
          ).find((prefix) => {
            if (Object.keys(currentPlan[planInterval].local).length === 1) {
              return true
            }

            return prefix.includes(planPrefix)
          })

          const planCodeGlobal = currentPlan[planInterval].global
            ? Object.keys(currentPlan[planInterval].global).find((prefix) => {
                if (
                  Object.keys(currentPlan[planInterval].global).length === 1
                ) {
                  return true
                }
                return prefix.includes(planPrefix)
              })
            : null

          return {
            ...newPlans,
            [currentPlanKey]: {
              ...currentPlan,
              [planInterval]: {
                ...(currentPlan[planInterval].global && {
                  global: {
                    [planCodeGlobal ?? '']: currentPlan[planInterval].global[
                      planCodeGlobal ?? ''
                    ] ?? {
                      currencies: { USD: 0 },
                    },
                  },
                }),
                local: {
                  [planCodeLocal ?? '']: currentPlan[planInterval].local[
                    planCodeLocal ?? ''
                  ] ?? {
                    currencies: { USD: 0 },
                  },
                },
              },
            },
          }
        },
        {},
      )

      const normalizedRegularTiers = Object.entries(
        plansWithLifetimeIncrease,
      ).reduce<Record<string, NormalizedTier>>(
        (newPlans, [tierKey, tierValue]) => {
          const normalizedTier = Object.entries(
            tierValue,
          ).reduce<NormalizedTier>((newTier, [planKey, planValue]) => {
            if (!planValue?.local && !planValue?.global) {
              return newTier
            }

            const planCodeLocal = Object.keys(planValue.local ?? {})[0]
            const planCodeGlobal = Object.keys(planValue.global ?? {})[0]

            if (!planCodeLocal && !planCodeGlobal) {
              return newTier
            }

            const localCurrencies = planValue.local?.[planCodeLocal]?.currencies
            const globalCurrencies =
              planValue.global?.[planCodeGlobal]?.currencies

            if (!localCurrencies && !globalCurrencies) {
              return newTier
            }

            return {
              ...newTier,
              [planKey]: {
                global: {
                  plan_code: planCodeGlobal,
                  currencies: globalCurrencies,
                },
                local: {
                  plan_code: planCodeLocal,
                  currencies: localCurrencies,
                },
              },
            }
          }, {})

          return { ...newPlans, [tierKey]: normalizedTier }
        },
        {},
      )

      const normalizedPlans: NormalizedPlans = {
        ...normalizedRegularTiers,
      }

      setPlans(normalizedPlans)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  return { isLoading, fetchPlans, setIsLoading }
}
